// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { DxWidgetUtils, IconConstants, Utils } from 'cc-common';

// Third party imports
import { DxFormComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { Subscription } from 'rxjs';

// Constant imports
import { Constants } from '../../../../../_constants/constants';
import { MessageInterval } from '../../../../../_constants/messages-intervals';

// Service imports
import { ServerErrorService } from '../../../../../_services/shared-services/server-error.service';
import { ARPaymentService } from '../../../_services/account-receivable/ar-payment.service';
import { ContextManagerService } from '../../../../../_services/data-services/context-manager.service';
import { PrecisionService } from '../../../../../_services/shared-services/precision.service';
import { HelperService } from '../../../../../_services/shared-services/helper.service';

// Models imports
import { ARPaymentParameterDto } from '../../../_models/account-receivable/ar-payment/ar-payment-parameter-dto';
import { ARPaymentEditorResultDto } from '../../../_models/account-receivable/ar-payment/ar-payment-editor-result-dto';
import { ARPaymentRawDto } from '../../../_models/account-receivable/ar-payment/ar-payment-raw-dto';
import { DimensionOptionCodeRawDto } from '../../../_models/common/dimensions/dimension-option-code-raw-dto';

@Component({
    selector: 'app-ar-payment-editor',
    templateUrl: './ar-payment-editor.component.html',
    styleUrls: ['./ar-payment-editor.component.scss']
})
export class ARPaymentEditorComponent implements OnInit {
    @ViewChild('paymentEditorForm', { static: false }) paymentEditorForm: DxFormComponent;
    @Input() visible = false;
    @Input() managementCompanyId: number;
    @Input() communityId: number;
    @Input() customerId: string;
    @Input() arPaymentParam: ARPaymentParameterDto;
    @Input() documentNo: string;
    @Output() onPaymentEditorClose = new EventEmitter<boolean>();

    popupTitle: string;
    defaultLabelLocation: string = Constants.defaultFormLabelLocation;
    loadingVisible = false;
    saveIcon = IconConstants.SAVE;
    cancelIcon = IconConstants.CLOSE;

    paymentEditorData: ARPaymentEditorResultDto;
    paymentInfo: ARPaymentRawDto;

    isAddPayment = false;
    glAccountColumns: any[];
    glAccountNumbers: string[];
    offsetGLAccountNumbers: string[];
    currencyFormat: any;
    contextSubscription: Subscription;

    constructor(
        private readonly arPaymentService: ARPaymentService,
        private readonly errorService: ServerErrorService,
        private readonly contextManagerService: ContextManagerService,
        private readonly precisionService: PrecisionService,
        private readonly helperService: HelperService
    ) {}

    ngOnInit() {
        const selectedContext = this.contextManagerService.getSelectedContext();
        if (!!selectedContext) {
            this.customerId = this.customerId || selectedContext.customerId;
            this.communityId = this.communityId || selectedContext.communityId;
            this.managementCompanyId = this.managementCompanyId || selectedContext.managementCompanyId;
        }

        if (Utils.notNullAndDefined(this.arPaymentParam)) {
            this.communityId = this.arPaymentParam.communityId;
            this.managementCompanyId = this.arPaymentParam.managementCompanyId;
            if (this.documentNo === 'Add Payment') {
                this.popupTitle = 'Add AR Payment';
                this.isAddPayment = true;
            } else {
                this.popupTitle = `Edit Payment ${this.documentNo}`;
                this.isAddPayment = false;
            }
            this.currencyFormat = this.precisionService.getCurrencyFormat();
            this.populatePaymentEditor();
            this.contextSubscription = this.contextManagerService.getContext().subscribe(() => {
                this.populatePaymentEditor();
            });
            this.getGLAccountColumns();
        } else {
            this.onHidden(null);
        }
    }

    onHidden(event): void {
        this.visible = false;
        this.onPaymentEditorClose.emit(event);
    }

    onGlAccountNumberSelection(e: any): void {
        if (!!e && !!e.selectedRow) {
            this.paymentInfo.glAccountNumber = e.selectedRow[0].glAccountNumber;
        }
    }

    onOffsetGlAccountNumberSelection(e: any): void {
        if (!!e && !!e.selectedRow) {
            this.paymentInfo.offsetGLAccountNumber = e.selectedRow[0].glAccountNumber;
        }
    }

    onSaveClick(): void {
        if (DxWidgetUtils.hasInstance(this.paymentEditorForm)) {
            const validator = this.paymentEditorForm.instance.validate();
            if (Utils.isTrue(validator.isValid)) {
                if (Utils.isTrue(this.isAddPayment)) {
                    if (this.paymentInfo.amount > 0) {
                        this.addARPaymentDetail();
                    } else {
                        notify(
                            'Payment Amount should be greater than zero.',
                            'error',
                            MessageInterval.errorMsgInterval
                        );
                    }
                } else {
                    this.updateARPaymentDetail();
                }
            }
        }
    }

    private displayError(error: any): void {
        this.loadingVisible = false;
        this.errorService.displayError(error);
    }

    private updateARPaymentDetail() {
        this.loadingVisible = true;
        const baseParam = this.prepareParameter();
        this.arPaymentService.updateARPaymentDetails(baseParam).subscribe(
            () => {
                notify('Payment detail updated successfully', 'success', MessageInterval.successMsgInterval);
                this.onHidden(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.displayError(error);
            }
        );
    }

    private addARPaymentDetail() {
        if (Utils.isValidRNumber(this.customerId) && Utils.notNullAndDefined(this.communityId)) {
            this.paymentInfo.customerCode = this.customerId;
            const baseParam = this.prepareParameter();

            this.loadingVisible = true;

            this.arPaymentService.addARPaymentDetails(baseParam).subscribe(
                () => {
                    notify('Payment detail added successfully', 'success', MessageInterval.successMsgInterval);
                    this.onHidden(true);
                    this.loadingVisible = false;
                },
                (error) => {
                    this.displayError(error);
                }
            );
        }
    }

    private prepareParameter(): ARPaymentRawDto {
        const baseParam: ARPaymentRawDto = new ARPaymentRawDto();

        baseParam.paymentId = this.paymentInfo.paymentId;
        baseParam.communityId = this.paymentInfo.communityId;
        baseParam.managementCompanyId = this.paymentInfo.managementCompanyId;

        // During ADD operation taking Payment description as Document number (Fixed DEV-3863)
        baseParam.documentNumber = Utils.isTrue(this.isAddPayment)
            ? this.paymentInfo.paymentDescription
            : this.paymentInfo.documentNumber;

        baseParam.paymentDescription = this.paymentInfo.paymentDescription;

        baseParam.documentDate = this.paymentInfo.documentDate;
        baseParam.postDate = this.paymentInfo.postDate;
        baseParam.description = this.paymentInfo.description;
        baseParam.paymentType = this.paymentInfo.paymentType;
        baseParam.subLedgerId = this.paymentInfo.subLedgerId;
        baseParam.amount = this.paymentInfo.amount;
        baseParam.glAccountNumber = this.paymentInfo.glAccountNumber;
        baseParam.offsetGLAccountNumber = this.paymentInfo.offsetGLAccountNumber;
        baseParam.customerCode = this.paymentInfo.customerCode;
        baseParam.dimensions = [];

        this.paymentEditorData.dimensions.forEach((dimension) => {
            if (Utils.notNullAndDefined(this.paymentInfo[dimension.code])) {
                const paymentInfoDimension = new DimensionOptionCodeRawDto();
                paymentInfoDimension.dimensionCode = dimension.code;
                paymentInfoDimension.dimensionOptionCode = this.paymentInfo[dimension.code];
                baseParam.dimensions.push(paymentInfoDimension);
            }
        });

        return baseParam;
    }

    private populatePaymentEditor(): void {
        this.loadingVisible = true;
        this.arPaymentService.getARPaymentEditor(this.arPaymentParam).subscribe(
            (result: ARPaymentEditorResultDto) => {
                this.paymentEditorData = result;
                this.paymentInfo = result.paymentInfo;
                this.glAccountNumbers = [result.paymentInfo.glAccountNumber];
                this.offsetGLAccountNumbers = [result.paymentInfo.offsetGLAccountNumber];
                // NOTE:- ANG-2633 show proper message only for Add Payment for Edit GLAccount is not editable. This functionality is different from V1.
                // if GL Account is inactive or disabled and used as Undeposited Fund Account at entity level
                if (Utils.isTrue(this.isAddPayment)) {
                    const isUndepositedFundAccountNumberExist = this.paymentEditorData.glAccounts.find((g) =>
                        Utils.equalsIgnoreCase(g.glAccountNumber, this.paymentEditorData.undepositedFundAccountNumber)
                    );
                    if (Utils.isNullOrUndefined(isUndepositedFundAccountNumberExist)) {
                        this.helperService
                            .getOkDialog(
                                'Error',
                                `${result.undepositedFundAccountNumber} GL Account is not enabled and used as “Undeposited Fund Account”,</br> Please check Entity-Configuration.`
                            )
                            .show()
                            .then(() => {
                                this.onHidden(false);
                            });
                    }
                }
                // ANG-2379: Added null check for dimensions to avoid null error
                if (
                    Utils.notNullAndDefined(this.paymentEditorData.dimensions) &&
                    Utils.notNullAndDefined(this.paymentInfo.dimensions)
                ) {
                    this.paymentEditorData.dimensions.forEach((dimension) => {
                        const selectedDimensionOptionCode = this.paymentInfo.dimensions.find((d) =>
                            Utils.equalsIgnoreCase(d.dimensionCode, dimension.code)
                        );

                        this.paymentInfo[dimension.code] = Utils.notNullAndDefined(selectedDimensionOptionCode)
                            ? selectedDimensionOptionCode.dimensionOptionCode
                            : null;
                    });
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.displayError(error);
            }
        );
    }

    private getGLAccountColumns(): void {
        this.glAccountColumns = [
            {
                dataField: 'title',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: 'GL Account Number'
            },
            {
                dataField: 'displayText',
                dataType: 'string',
                visible: false
            }
        ];
    }
}
